//src/pages/Movimento/edit/modules/distribuicaoFiscal/DocumentosFiscaisDialog/index.js

import React, { useMemo, useState } from 'react';

import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    ThemeProvider,
} from '@mui/material';

import { Download, Refresh } from '@mui/icons-material';

import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import CustomDialog from '../../../../../../layout/CustomDialog';

import { getMRTDefaultProps } from '../../../../../../../helpers/mrt/getMRTDefaultProps';

export default function DocumentosFiscaisDialog({ module, theme }) {
    const {
        open,

        visualizacao,

        resumos,
        totalResumos,

        limiteDownloadLote,

        documentosSemMovimento,
        totalDocumentosSemMovimento,

        pagination,

        progressoDownload,
    } = module.state;

    const { isLoading, isRefetching, isImporting, isError, isDownloadingBatch } = module.flags;
    const [resumoSelecionado, setResumoSelecionado] = useState(null);
    const [rowSelection, setRowSelection] = useState({});

    const [resumosSelecionados, setResumosSelecionados] = useState([]);

    const quantidadeSelecionada = Object.values(rowSelection).filter(Boolean).length;

    const limiteDownloadSeguro =
        Number.isInteger(Number(limiteDownloadLote)) && Number(limiteDownloadLote) > 0 ? Number(limiteDownloadLote) : 1;

    const alterarRowSelection = (updater) => {
        setRowSelection((selecaoAtual) => {
            const proximaSelecao = typeof updater === 'function' ? updater(selecaoAtual) : updater;

            const selecionados = Object.entries(proximaSelecao || {}).filter(([, selecionado]) => Boolean(selecionado));

            if (selecionados.length <= limiteDownloadSeguro) {
                return proximaSelecao;
            }

            /*
             * Proteção adicional para ações coletivas ou
             * atualizações programáticas do MRT.
             */
            return Object.fromEntries(selecionados.slice(0, limiteDownloadSeguro));
        });
    };

    const {
        fechar,
        atualizar,
        importarResumo,
        carregarDocumentoSemMovimento,
        setPagination,
        alterarVisualizacao,
        baixarResumosSelecionados,
    } = module.actions;
    const exibindoResumos = visualizacao === 'RESUMOS';

    const mrtDefault = getMRTDefaultProps({
        mrt: module.mrt,

        parametro: module.parametro,
    });
    const columns = useMemo(
        () => [
            {
                accessorKey: 'dataEmissao',
                header: 'Emissão',
                size: 110,
                Cell: ({ cell }) => {
                    const value = cell.getValue();

                    if (!value) return '';

                    const data = new Date(value);

                    if (Number.isNaN(data.getTime())) {
                        return '';
                    }

                    return data.toLocaleDateString('pt-BR');
                },
            },
            ...(exibindoResumos
                ? [
                      {
                          accessorKey: 'diasRestantesCienciaOperacao',

                          header: 'Prazo',

                          size: 110,

                          enableColumnFilter: false,

                          enableSorting: false,

                          Cell: ({ cell, row }) => {
                              const diasRestantes = Number(cell.getValue());

                              if (!Number.isFinite(diasRestantes)) {
                                  return '';
                              }

                              const limite = row.original?.cienciaOperacaoLimiteEm;

                              const dataLimite = limite ? new Date(limite) : null;

                              const limiteFormatado =
                                  dataLimite && !Number.isNaN(dataLimite.getTime()) ? dataLimite.toLocaleString('pt-BR') : '';

                              const color = diasRestantes <= 1 ? 'error' : diasRestantes <= 3 ? 'warning' : 'success';

                              return (
                                  <Chip
                                      size="small"
                                      variant="outlined"
                                      color={color}
                                      label={diasRestantes === 1 ? '1 dia restante' : `${diasRestantes} dias restantes`}
                                      title={limiteFormatado ? `Prazo estimado até ${limiteFormatado}` : undefined}
                                  />
                              );
                          },
                      },
                  ]
                : []),
            {
                accessorKey: 'emitenteNome',
                header: 'Emitente',
                size: 280,
                muiTableBodyCellProps: ({ row }) => ({
                    onClick: () => {
                        console.info(row.original);
                    },
                }),
            },
            {
                accessorKey: 'valorNota',
                header: 'Valor',
                size: 110,
                muiTableBodyCellProps: {
                    align: 'right',
                },
                Cell: ({ cell }) => {
                    const value = Number(cell.getValue());

                    if (!Number.isFinite(value)) {
                        return '';
                    }

                    return value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                    });
                },
            },
            {
                accessorKey: 'tipoDocumento',
                header: 'Tipo',
                size: 130,
                Cell: ({ cell }) => {
                    const tipoDocumento = String(cell.getValue() || '').trim();

                    const descricoes = {
                        RES_NFE: 'Resumo NF-e',
                        PROC_NFE: 'XML completo',
                    };

                    return descricoes[tipoDocumento] || tipoDocumento;
                },
            },
            ...(exibindoResumos
                ? []
                : [
                      {
                          accessorKey: 'numero',
                          header: 'Número',
                          size: 90,
                      },
                      {
                          accessorKey: 'serie',
                          header: 'Série',
                          size: 70,
                      },
                  ]),

            {
                accessorKey: 'chaveAcesso',
                header: 'Chave de acesso',
                size: 330,
            },
        ],
        [exibindoResumos]
    );

    const linhas = exibindoResumos ? resumos : documentosSemMovimento;

    const totalLinhas = exibindoResumos ? totalResumos : totalDocumentosSemMovimento;

    const confirmarDownloadSelecionados = () => {
        const selecionados = Object.entries(rowSelection)
            .filter(([, selecionado]) => selecionado)
            .map(([id]) => ({
                id: Number(id),
            }))
            .filter((resumo) => Number.isInteger(resumo.id) && resumo.id > 0);

        setResumosSelecionados(selecionados);
    };
    return (
        <Dialog open={open} onClose={fechar} fullWidth maxWidth="xl">
            <DialogTitle>Notas fiscais disponíveis</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {isError && <Alert severity="error">Não foi possível carregar as notas fiscais disponíveis.</Alert>}

                    <Tabs
                        value={visualizacao}
                        onChange={(_, value) => {
                            setResumoSelecionado(null);

                            setResumosSelecionados([]);

                            setRowSelection({});

                            alterarVisualizacao(value);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab value="RESUMOS" label={`Notas disponíveis (${totalResumos})`} />

                        <Tab value="XMLS_BAIXADOS" label={`XMLs baixados (${totalDocumentosSemMovimento})`} />
                    </Tabs>

                    {isDownloadingBatch && (
                        <Stack spacing={1}>
                            <Alert severity="info">
                                Baixando documento {progressoDownload.atual} de {progressoDownload.total}.
                            </Alert>

                            <LinearProgress
                                variant="determinate"
                                value={
                                    progressoDownload.total > 0 ? (progressoDownload.atual / progressoDownload.total) * 100 : 0
                                }
                            />
                        </Stack>
                    )}

                    {exibindoResumos && (
                        <Alert severity={quantidadeSelecionada >= limiteDownloadSeguro ? 'warning' : 'info'}>
                            Selecione no máximo {limiteDownloadSeguro} nota(s) por lote.
                        </Alert>
                    )}
                    <ThemeProvider theme={theme}>
                        <MaterialReactTable
                            {...mrtDefault}
                            columns={columns}
                            data={linhas}
                            rowCount={totalLinhas}
                            enableRowSelection={(row) =>
                                exibindoResumos && (row.getIsSelected() || quantidadeSelecionada < limiteDownloadSeguro)
                            }
                            onRowSelectionChange={alterarRowSelection}
                            enableSelectAll={false}
                            getRowId={(row) => String(row.id)}
                            renderTopToolbarCustomActions={() =>
                                exibindoResumos ? (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        color="info"
                                        startIcon={<Download />}
                                        onClick={confirmarDownloadSelecionados}
                                        disabled={quantidadeSelecionada === 0 || isImporting || isDownloadingBatch}
                                    >
                                        Baixar selecionadas ({quantidadeSelecionada})
                                    </Button>
                                ) : null
                            }
                            renderRowActions={({ row }) => (
                                <Button
                                    type="button"
                                    variant="contained"
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => setResumoSelecionado(row.original)}
                                    disabled={isImporting || isDownloadingBatch}
                                >
                                    {exibindoResumos ? 'Importar' : 'Carregar XML'}
                                </Button>
                            )}
                            onPaginationChange={setPagination}
                            state={{
                                ...mrtDefault.state,

                                pagination,

                                rowSelection,

                                isLoading,

                                showProgressBars: isRefetching || isDownloadingBatch,

                                showAlertBanner: isError,
                            }}
                            muiTableContainerProps={{
                                sx: {
                                    maxHeight: '60vh',
                                },
                            }}
                            renderEmptyRowsFallback={() => (
                                <Alert severity="info" sx={{ margin: 2 }}>
                                    {exibindoResumos
                                        ? 'Nenhum resumo fiscal aguardando XML.'
                                        : 'Nenhum XML baixado aguardando entrada.'}
                                </Alert>
                            )}
                        />
                    </ThemeProvider>
                </Stack>
            </DialogContent>

            {resumoSelecionado && (
                <CustomDialog
                    open={!!resumoSelecionado}
                    title={exibindoResumos ? 'Obter XML da nota fiscal' : 'Carregar XML já baixado'}
                    content={
                        <>
                            {exibindoResumos
                                ? 'Deseja consultar e importar o XML desta nota?'
                                : 'Deseja carregar este XML já baixado para a entrada da nota?'}
                            <br />
                            <br />
                            <strong>{resumoSelecionado.emitenteNome}</strong>
                            <br />
                            Chave: {resumoSelecionado.chaveAcesso}
                        </>
                    }
                    onClose={() => setResumoSelecionado(null)}
                    onConfirm={async () => {
                        const processado = exibindoResumos
                            ? await importarResumo(resumoSelecionado)
                            : await carregarDocumentoSemMovimento(resumoSelecionado);

                        if (processado) {
                            setResumoSelecionado(null);
                        }
                    }}
                    disableConfirm={isImporting}
                    loading={isImporting}
                    confirmLabel={exibindoResumos ? 'Obter XML' : 'Carregar XML'}
                    confirmColor="primary"
                />
            )}
            {resumosSelecionados.length > 0 && (
                <CustomDialog
                    open
                    title="Baixar XMLs selecionados"
                    content={
                        <>
                            Deseja obter e persistir os XMLs das notas selecionadas?
                            <br />
                            <br />
                            <strong>Quantidade: {resumosSelecionados.length}</strong>
                            <br />
                            <br />
                            Os XMLs disponíveis serão transferidos para a aba “XMLs baixados”. Nenhum Movimento será preenchido
                            automaticamente.
                        </>
                    }
                    onClose={() => setResumosSelecionados([])}
                    onConfirm={async () => {
                        const resultado = await baixarResumosSelecionados(resumosSelecionados);

                        if (resultado) {
                            if (resultado.interrompidoPorBloqueio) {
                                const idsNaoProcessados = Array.isArray(resultado.idsNaoProcessados)
                                    ? resultado.idsNaoProcessados
                                    : [];

                                setRowSelection(Object.fromEntries(idsNaoProcessados.map((id) => [String(id), true])));
                            } else {
                                setRowSelection({});
                            }

                            setResumosSelecionados([]);
                        }
                    }}
                    disableConfirm={isDownloadingBatch}
                    loading={isDownloadingBatch}
                    confirmLabel="Baixar XMLs"
                    confirmColor="primary"
                />
            )}
            <DialogActions>
                <Button
                    type="button"
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={atualizar}
                    disabled={isLoading || isRefetching}
                >
                    Atualizar
                </Button>

                <Button type="button" variant="contained" color="secondary" onClick={fechar}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
